import { useEffect, useState, useCallback } from "react";
import { Navigate, Link, useNavigate, useParams } from "react-router-dom";
import { Section, Eyebrow, H2, GlassCard } from "../../components/site/Primitives";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { log } from "../../lib/logger";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Save, ArrowLeft, BarChart3, Building2, GraduationCap, Calendar, Briefcase, FileText, Inbox, Crown } from "lucide-react";
import FounderDashboard from "./FounderDashboard";

const NAV = [
  { slug: "overview",    label: "Overview",    icon: BarChart3 },
  { slug: "properties",  label: "Properties",  icon: Building2 },
  { slug: "courses",     label: "Courses",     icon: GraduationCap },
  { slug: "events",      label: "Events",      icon: Calendar },
  { slug: "jobs",        label: "Jobs",        icon: Briefcase },
  { slug: "content",     label: "Site Content",icon: FileText },
  { slug: "submissions", label: "Submissions", icon: Inbox },
];
const FOUNDER_NAV = { slug: "founder", label: "Founder", icon: Crown };

const SUPER_EMAIL = "travis.mcgruder@220inc.com";
const isSuperAdmin = (u) => !!u && (u.is_super_admin === true || (u.email || "").toLowerCase() === SUPER_EMAIL);

export default function AdminPanel() {
  const { tab = "overview" } = useParams();
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin && !["admin", "property_manager"].includes(user.role)) {
    return (
      <Section className="pt-40">
        <Eyebrow>Admin Panel</Eyebrow>
        <H2>Restricted</H2>
        <p className="mt-4 text-white/60">Your current role is <strong>{user.role}</strong>. Switch to Admin from <Link className="text-gold" to="/portal">/portal</Link> to access this area.</p>
      </Section>
    );
  }

  return (
    <Section className="pt-32" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <Eyebrow>Admin · 220 Inc.</Eyebrow>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Operations Console</h1>
        </div>
        <Link to="/portal" className="text-sm text-white/60 hover:text-gold flex items-center gap-2"><ArrowLeft size={14} /> Back to Portal</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav className="border border-white/10">
            {(isSuperAdmin(user) ? [FOUNDER_NAV, ...NAV] : NAV).map((n) => {
              const Icon = n.icon;
              const active = tab === n.slug;
              const isFounder = n.slug === "founder";
              return (
                <Link key={n.slug} to={`/portal/admin/${n.slug}`}
                  className={`flex items-center gap-3 px-5 py-4 border-b border-white/10 last:border-b-0 text-sm transition-colors ${
                    active
                      ? (isFounder ? "bg-gold/10 text-gold border-l-2 border-l-gold" : "bg-gold/5 text-gold border-l-2 border-l-gold")
                      : "text-white/75 hover:bg-white/[0.03]"
                  }`} data-testid={`admin-nav-${n.slug}`}>
                  <Icon size={16} /> {n.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        <div className="lg:col-span-9">
          {tab === "founder" && isSuperAdmin(user) && <FounderDashboard user={user} />}
          {tab === "overview" && <Overview />}
          {tab === "properties" && <CRUD title="Properties" endpoint="/admin/properties" fields={PROPERTY_FIELDS} testIdPrefix="properties" />}
          {tab === "courses" && <CRUD title="Courses" endpoint="/admin/courses" fields={COURSE_FIELDS} testIdPrefix="courses" />}
          {tab === "events" && <CRUD title="Club Events" endpoint="/admin/events" fields={EVENT_FIELDS} testIdPrefix="events" />}
          {tab === "jobs" && <CRUD title="Job Listings" endpoint="/admin/jobs" fields={JOB_FIELDS} testIdPrefix="jobs" />}
          {tab === "content" && <ContentEditor />}
          {tab === "submissions" && <SubmissionsBrowser />}
        </div>
      </div>
    </Section>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => log.warn("admin overview fetch failed", e?.message)); }, []);
  if (!data) return <div className="text-white/50">Loading…</div>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-8">
        {Object.entries(data.counts).map(([k, v]) => (
          <div key={k} className="bg-[#0c0c0e] p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 capitalize">{k.replace(/_/g, " ")}</div>
            <div className="font-display text-2xl text-gold">{v}</div>
          </div>
        ))}
      </div>
      <Eyebrow>Recent Housing Applications</Eyebrow>
      <GlassCard className="mt-4 divide-y divide-white/10">
        {(data.recent_housing || []).map((h) => (
          <div key={h.id} className="p-5 flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-lg">{h.full_name}</div>
              <div className="text-xs text-white/40 mt-1">{h.email} · {h.housing_type}</div>
            </div>
            <span className={`text-xs uppercase tracking-[0.2em] px-3 py-1 ${h.emergency ? "text-red-400 border border-red-400/40" : "text-gold border border-gold/40"}`}>
              {h.status}
            </span>
          </div>
        ))}
        {!data.recent_housing?.length && <div className="p-6 text-sm text-white/50">No applications yet.</div>}
      </GlassCard>
    </div>
  );
}

// Field schemas
const TYPE_TEXT = "text", TYPE_NUM = "number", TYPE_BOOL = "boolean", TYPE_TEXTAREA = "textarea", TYPE_SELECT = "select";

const PROPERTY_FIELDS = [
  { key: "title", label: "Title", type: TYPE_TEXT, required: true, default: "" },
  { key: "type",  label: "Type",  type: TYPE_SELECT, options: ["residential", "commercial", "land"], default: "residential" },
  { key: "city",  label: "City",  type: TYPE_TEXT, required: true, default: "Columbus, GA" },
  { key: "beds",  label: "Beds",  type: TYPE_NUM, default: 0 },
  { key: "baths", label: "Baths", type: TYPE_NUM, default: 0 },
  { key: "sqft",  label: "Sqft",  type: TYPE_NUM, default: 0 },
  { key: "price", label: "Price (USD)", type: TYPE_NUM, default: 0 },
  { key: "for",   label: "For",   type: TYPE_SELECT, options: ["rent", "sale", "program"], default: "rent" },
  { key: "image", label: "Image URL", type: TYPE_TEXT, default: "" },
  { key: "lat",   label: "Latitude",  type: TYPE_NUM, default: 32.46 },
  { key: "lng",   label: "Longitude", type: TYPE_NUM, default: -84.99 },
  { key: "description", label: "Description", type: TYPE_TEXTAREA, default: "" },
  { key: "published",   label: "Published",   type: TYPE_BOOL, default: true },
];

const COURSE_FIELDS = [
  { key: "title", label: "Title", type: TYPE_TEXT, required: true, default: "" },
  { key: "duration_weeks", label: "Duration (weeks)", type: TYPE_NUM, default: 4 },
  { key: "format", label: "Format", type: TYPE_SELECT, options: ["On-site", "Online", "Hybrid"], default: "Hybrid" },
  { key: "veteran_priority", label: "Veteran Priority", type: TYPE_BOOL, default: false },
  { key: "description", label: "Description", type: TYPE_TEXTAREA, default: "" },
  { key: "published", label: "Published", type: TYPE_BOOL, default: true },
];

const EVENT_FIELDS = [
  { key: "title", label: "Title", type: TYPE_TEXT, required: true, default: "" },
  { key: "date",  label: "Date (YYYY-MM-DD)", type: TYPE_TEXT, required: true, default: "" },
  { key: "lounge", label: "Lounge", type: TYPE_SELECT, options: ["Main Floor", "Fire Lounge", "Ice Lounge"], default: "Main Floor" },
  { key: "tickets_from", label: "Tickets From (USD)", type: TYPE_NUM, default: 0 },
  { key: "image", label: "Image URL", type: TYPE_TEXT, default: "" },
  { key: "description", label: "Description", type: TYPE_TEXTAREA, default: "" },
  { key: "published", label: "Published", type: TYPE_BOOL, default: true },
];

const JOB_FIELDS = [
  { key: "title", label: "Title", type: TYPE_TEXT, required: true, default: "" },
  { key: "subsidiary", label: "Subsidiary", type: TYPE_TEXT, required: true, default: "220 Construction" },
  { key: "location",   label: "Location",   type: TYPE_TEXT, default: "Columbus, GA" },
  { key: "type",       label: "Type",       type: TYPE_SELECT, options: ["full_time", "part_time", "contract", "internship"], default: "full_time" },
  { key: "veteran_priority", label: "Veteran Priority", type: TYPE_BOOL, default: false },
  { key: "description", label: "Description", type: TYPE_TEXTAREA, default: "" },
  { key: "published",   label: "Published",   type: TYPE_BOOL, default: true },
];

function CRUD({ title, endpoint, fields, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(endpoint); setItems(data.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load."); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async (vals) => {
    setBusy(true);
    try {
      if (editing === "new") await api.post(endpoint, vals);
      else await api.put(`${endpoint}/${editing.id}`, vals);
      toast.success("Saved.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const filtered = items.filter((it) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return Object.values(it).some((v) => String(v ?? "").toLowerCase().includes(q));
  });

  return (
    <div data-testid={`admin-crud-${testIdPrefix}`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-2xl">{title}</h2>
        <div className="flex items-center gap-3">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search…"
            className="bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold"
            data-testid={`${testIdPrefix}-search`} />
          <button onClick={() => setEditing("new")} className="btn-primary text-sm" data-testid={`${testIdPrefix}-new`}>
            <Plus size={14} /> New
          </button>
        </div>
      </div>

      <div className="border border-white/10 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/[0.03] text-left">
              {fields.slice(0, 4).map((f) => (
                <th key={f.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-white/40">{f.label}</th>
              ))}
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-white/40">Status</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-white/40 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-white/10 hover:bg-white/[0.02]">
                {fields.slice(0, 4).map((f) => (
                  <td key={f.key} className="px-4 py-3 text-white/85">{formatCellValue(it[f.key])}</td>
                ))}
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${it.published ? "text-gold border-gold/40" : "text-white/40 border-white/10"}`}>
                    {it.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(it)} className="text-white/60 hover:text-gold" data-testid={`${testIdPrefix}-edit-${it.id}`}><Pencil size={14} /></button>
                    <button onClick={() => del(it.id)} className="text-white/60 hover:text-red-400" data-testid={`${testIdPrefix}-del-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={fields.slice(0, 4).length + 2} className="px-4 py-6 text-center text-white/40">No items.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditDialog
          fields={fields}
          initial={dialogInitial(editing, fields)}
          onCancel={() => setEditing(null)}
          onSave={save}
          busy={busy}
          title={dialogTitle(editing, title)}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}

function formatCellValue(v) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v ?? "—");
}

function dialogInitial(editing, fields) {
  if (editing === "new") return Object.fromEntries(fields.map((f) => [f.key, f.default]));
  return editing;
}

function dialogTitle(editing, title) {
  const noun = title.endsWith("s") ? title.slice(0, -1) : title;
  return editing === "new" ? `New ${noun}` : `Edit ${noun}`;
}

function FieldInput({ f, value, onChange }) {
  if (f.type === TYPE_TEXTAREA) {
    return (
      <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold" />
    );
  }
  if (f.type === TYPE_BOOL) {
    return (
      <label className="flex items-center gap-3">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
        <span className="text-sm text-white/80">{f.label}</span>
      </label>
    );
  }
  if (f.type === TYPE_SELECT) {
    return (
      <select value={value ?? f.default} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold">
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={f.type === TYPE_NUM ? "number" : "text"} value={value ?? ""}
      required={f.required} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold" />
  );
}

function EditDialog({ fields, initial, onCancel, onSave, busy, title, testIdPrefix }) {
  const [vals, setVals] = useState(initial);
  const set = (k, v) => setVals((x) => ({ ...x, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const out = { ...vals };
    fields.forEach((f) => {
      if (f.type === TYPE_NUM) out[f.key] = out[f.key] === "" || out[f.key] == null ? 0 : Number(out[f.key]);
      if (f.type === TYPE_BOOL) out[f.key] = !!out[f.key];
    });
    onSave(out);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm p-6 flex items-center justify-center overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
      <form onSubmit={submit} className="w-full max-w-2xl bg-[#0c0c0e] border border-white/10 p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">{title}</h3>
          <button type="button" onClick={onCancel} className="text-white/60 hover:text-white"><X /></button>
        </div>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{f.label}</label>
              <FieldInput f={f} value={vals[f.key]} onChange={(v) => set(f.key, v)} />
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid={`${testIdPrefix}-save`}>
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContentEditor() {
  return (
    <div className="space-y-10">
      <ContentBlock keyName="founder" title="Founder Section (About page)" fields={["title", "subtitle", "body", "image"]} />
      <ContentBlock keyName="mission" title="Mission Statement (Home page)" fields={["title", "body"]} />
    </div>
  );
}

function ContentBlock({ keyName, title, fields }) {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/content/${keyName}`).then((r) => setC(r.data || {})).catch((e) => log.warn("content fetch failed", keyName, e?.message)); }, [keyName]);
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(fields.map((f) => [f, c[f] ?? ""]));
      await api.put(`/admin/content/${keyName}`, payload);
      toast.success("Saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  return (
    <GlassCard className="p-6" data-testid={`content-${keyName}`}>
      <h3 className="font-display text-xl mb-4">{title}</h3>
      <div className="space-y-4">
        {fields.map((f) => (
          <div key={f}>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{f}</label>
            {f === "body" ? (
              <textarea rows={5} value={c[f] || ""} onChange={(e) => set(f, e.target.value)}
                className="w-full bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold" />
            ) : (
              <input value={c[f] || ""} onChange={(e) => set(f, e.target.value)}
                className="w-full bg-[#0e0e10] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-gold" />
            )}
          </div>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="btn-primary text-sm mt-5" data-testid={`content-save-${keyName}`}>
        <Save size={14} /> {busy ? "Saving…" : "Save"}
      </button>
    </GlassCard>
  );
}

const SUB_KINDS = [
  { v: "contacts", l: "Contact Messages" },
  { v: "housing_applications", l: "Housing Applications" },
  { v: "donations", l: "Donations" },
  { v: "investor_inquiries", l: "Investor Inquiries" },
  { v: "career_applications", l: "Career Applications" },
  { v: "club_reservations", l: "Club Reservations" },
  { v: "contractor_bids", l: "Contractor Bids" },
  { v: "maintenance_tickets", l: "Maintenance Tickets" },
  { v: "volunteers", l: "Volunteers" },
  { v: "payment_transactions", l: "Payments" },
];

function SubmissionsBrowser() {
  const [kind, setKind] = useState("housing_applications");
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/admin/submissions/${kind}`).then((r) => setItems(r.data.items)).catch((e) => log.warn("submissions fetch failed", kind, e?.message)); }, [kind]);
  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap gap-2 mb-6">
        {SUB_KINDS.map((k) => (
          <button key={k.v} onClick={() => setKind(k.v)}
            className={`px-3 py-2 text-xs border transition-colors ${
              kind === k.v ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
            }`}>{k.l}</button>
        ))}
      </div>
      <div className="border border-white/10 divide-y divide-white/10">
        {items.map((it) => (
          <div key={it.id} className="p-4 text-sm flex items-center justify-between gap-4">
            <div className="font-mono text-xs text-white/40">{it.id}</div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-white/85">
              {Object.entries(it).filter(([k]) => !["id", "created_at", "_id"].includes(k)).slice(0, 4).map(([k, v]) => (
                <div key={k}><span className="text-white/40 text-[10px] uppercase tracking-[0.15em] mr-1">{k}:</span>{String(v).slice(0, 60)}</div>
              ))}
            </div>
            <div className="text-xs text-white/40">{new Date(it.created_at).toLocaleString()}</div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-sm text-white/40 text-center">No submissions yet.</div>}
      </div>
    </div>
  );
}
